/**
 * Display available "Payment Boni" as dynamic list.
 *
 * @author Jacob Viertel <jv@onscreen.net>
 * @since 3.99.0
 * @module
 */
import {AttributeLink, AttributeSetup, link, NodeUpgrade, CONNECT} from '@acng/frontend-stargazer';
import {Watch} from '@acng/frontend-relativity';
import {IS, guard} from '@acng/frontend-rubicon';
// bounty
import {find} from '@acng/frontend-bounty/dom/template.js';
import {TITLE, get} from '@acng/frontend-bounty/dom/attribute.js';
import {attributeSelector} from '@acng/frontend-bounty/dom/query.js';
import {append} from '@acng/frontend-bounty/dom/move.js';
import {noop} from '@acng/frontend-bounty/std/value.js';
import {size} from '@acng/frontend-bounty/std/array.js';
import {hide, show} from '@acng/frontend-bounty/style/element.js';

import {t} from '../../locale/config/translate.js';
import {Actor} from '../../layout/context/list.js';
import {ctxBonusList} from '../context/bonus-list.js';
import {teaser} from '../service/template.js';
import {BONUS_TEASER_FORMAT} from '../model/bonus.js';

/**
 * Provide the {@link ctxBonusList} as {@link NovaList}.
 *
 * > The implementation is not general but sufficient for *ACNG-8871*.
 * - The image format is hardcoded to "frontpage-bottom".
 * - The list is filtered, since that format is not available for all boni and all portals.
 * - It is only available on "EAM" for "50for250" and "WellCum".
 *
 * Hide the element if the list is empty.
 *
 * @group DOM Attribute
 * @see [ACNG-8871](https://ivg.atlassian.net/browse/ACNG-8871)
 * @example
 * ```html
 * <nova-list payment-bonus-list>...</nova-list>
 * ```
 */
export const PAYMENT_BONUS_LIST = 'payment-bonus-list';

AttributeSetup(PAYMENT_BONUS_LIST, (name) => {
  const watch = Watch(`[${name}]`, ctxBonusList);
  const publish = Actor();

  AttributeLink(name, (element, format) => {
    ASSERT: guard(format, BONUS_TEASER_FORMAT);

    watch(element, (element, bonusList) => {
      ASSERT: guard(element, IS(HTMLElement));

      if (size(bonusList) > 0) {
        show(element);
      } else {
        hide(element);
      }

      publish(
        element,
        async (offset) => (offset ? [] : bonusList),
        async (bonus) => {
          // Just test if the image is configured. `getImageUrl` will throw if not.
          await bonus.getImageUrl(format);

          const engine = await teaser.render(bonus.name, format);

          const titleElement = find(engine.template, attributeSelector(TITLE));
          const title =
            (titleElement && get(titleElement, TITLE)) ?? (await t(['payment', 'bonus', 'otmHeading']));

          return (element, linker) => {
            NodeUpgrade(linker, {
              title,
              /**
               * @type {import('@acng/frontend-stargazer').Controller}
               */ 
              teaser: {
                [CONNECT](element) {
                  append(element, link(engine, {}, element, noop));
                  engine.connect();

                  return () => engine.disconnect();
                },
              }
            });
          };
        }
      );
    });
  });
});
